import type { z } from 'zod';
import { zOwnerWebDashboardResponse } from '@argink/shared/owner-web';
import type {
  OwnerWebDashboardAtKind,
  OwnerWebDashboardAttentionKind,
} from '@argink/shared/owner-web';
import {
  ownerWebEnvelopeListSchema,
  ownerWebEnvelopeSummarySchema,
  ownerWebEnvelopeViewSchema,
  sessionInfoDtoSchema,
  verificationReportDtoSchema,
  type CreateEnvelopePayload,
} from './schemas';
import {
  ContractViolationError,
  type DashboardAtKind,
  type DashboardAttentionKind,
  type DashboardSnapshot,
  type EnvelopeDetail,
  type EnvelopeDetailDocument,
  type EnvelopeListItem,
  type EnvelopeListResponse,
  type EnvelopeSummary,
  type OwnerWebCreateEnvelopePayload,
  type OwnerWebDashboardResponse,
  type OwnerWebEnvelopeDetailDocument,
  type OwnerWebEnvelopeList,
  type OwnerWebEnvelopeListItem,
  type OwnerWebEnvelopeSummary,
  type OwnerWebEnvelopeView,
  type OwnerWebParticipantInput,
  type OwnerWebParticipantView,
  type OwnerWebResendRequest,
  type OwnerWebStartEnvelopeRequest,
  type Participant,
  type SessionInfo,
  type VerificationReport,
  type VerificationReportDto,
  type VisualPlacement,
} from './types';
import { stampPlacement, stampPresetOf, type StampPreset } from './visual-stamp';
import { signedDocumentValidationDtoSchema } from './schemas';
import type { SignedDocumentValidation } from './types';

/**
 * **Adapter canônico** (DI2-2) — o ÚNICO lugar do front onde nome de campo muda
 * de forma. O backend não empobrece o wire para caber na UI (snake_case é o
 * padrão de `/v1` e de todo o resto da API); a UI não espalha `snake_to_camel`
 * pelas telas. A tradução acontece aqui, nos dois sentidos:
 *
 * ```
 * DTO do backend  → adapter → modelo de UI
 * form da UI      → adapter → DTO do backend
 * ```
 *
 * Duas regras que este arquivo carrega inteiras:
 *
 *  - **nulidade é informação.** `expires_at`, `document.name`, `email`,
 *    `manifestHash`, `manifestSchemaVersion` e `sealKeyId` podem ser `null` de
 *    verdade. Nada aqui mascara com `!` nem com string vazia — quem exibe usa
 *    `documentDisplayName`/`participantEmailDisplay` (`lib/copy.ts`);
 *  - **fail-closed.** Resposta fora do contrato (campo faltando, tipo errado,
 *    status que este cliente não conhece) vira `ContractViolationError`, não um
 *    palpite. Ver `./status.ts` para o porquê de não existir fallback silencioso.
 *
 * O par de funções `toX` / `fromX` existe para o round-trip ser testável:
 * `fromX(toX(dto))` devolve o DTO original.
 */

// ---------------------------------------------------------------- validação

function valueAtPath(root: unknown, path: readonly (string | number)[]): unknown {
  let current: unknown = root;
  for (const step of path) {
    if (current === null || current === undefined) return current;
    current = (current as Record<string | number, unknown>)[step];
  }
  return current;
}

/** Roda o schema e converte a falha em `ContractViolationError` com o caminho
 * exato e o valor recebido — mensagem de diagnóstico, não erro genérico. */
export function parseDto<T extends z.ZodTypeAny>(
  schema: T,
  raw: unknown,
  label: string,
): z.infer<T> {
  const parsed = schema.safeParse(raw);
  if (parsed.success) return parsed.data as z.infer<T>;

  const issue = parsed.error.issues[0];
  const path = [label, ...(issue?.path ?? [])].join('.');
  throw new ContractViolationError(
    path,
    issue ? valueAtPath(raw, issue.path) : raw,
    issue?.message,
  );
}

// ---------------------------------------------------------------- participante

export function toParticipant(dto: OwnerWebParticipantView): Participant {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    role: dto.role,
    stage: dto.stage,
    requiredModality: dto.required_signature_level,
    actualModality: dto.actual_signature_level,
    status: dto.status,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    channelVerifiedAt: dto.channel_verified_at,
    signedAt: dto.signed_at,
    declineReason: dto.decline_reason,
    visualStamp: dto.visual,
  };
}

export function fromParticipant(model: Participant): OwnerWebParticipantView {
  return {
    id: model.id,
    name: model.name,
    email: model.email,
    role: model.role,
    stage: model.stage,
    required_signature_level: model.requiredModality,
    actual_signature_level: model.actualModality,
    status: model.status,
    created_at: model.createdAt,
    updated_at: model.updatedAt,
    channel_verified_at: model.channelVerifiedAt,
    signed_at: model.signedAt,
    decline_reason: model.declineReason,
    visual: model.visualStamp,
  };
}

// ---------------------------------------------------------------- envelope (detail)

function toEnvelopeDetailDocument(dto: OwnerWebEnvelopeDetailDocument): EnvelopeDetailDocument {
  return {
    id: dto.id,
    name: dto.name,
    format: dto.format,
    hashSha256: dto.hash_sha256,
    pageCount: dto.page_count,
    createdAt: dto.created_at,
    signed: dto.signed,
  };
}

function fromEnvelopeDetailDocument(model: EnvelopeDetailDocument): OwnerWebEnvelopeDetailDocument {
  return {
    id: model.id,
    name: model.name,
    format: model.format,
    hash_sha256: model.hashSha256,
    page_count: model.pageCount,
    created_at: model.createdAt,
    signed: model.signed,
  };
}

export function toEnvelopeDetail(dto: OwnerWebEnvelopeView): EnvelopeDetail {
  const participants = dto.participants.map(toParticipant);
  return {
    id: dto.id,
    status: dto.status,
    ceremonyMode: dto.ceremony_mode,
    quorum: dto.quorum,
    currentStage: dto.current_stage,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    deadlineAt: dto.expires_at,
    document: dto.document ? toEnvelopeDetailDocument(dto.document) : null,
    participants,
    // Contagem, não regra: quem decide quem assina é o backend (`current_stage`).
    participantsTotal: participants.filter((p) => p.role === 'signer').length,
    participantsSigned: participants.filter((p) => p.role === 'signer' && p.status === 'signed')
      .length,
  };
}

export function fromEnvelopeDetail(model: EnvelopeDetail): OwnerWebEnvelopeView {
  return {
    id: model.id,
    status: model.status,
    ceremony_mode: model.ceremonyMode,
    quorum: model.quorum,
    current_stage: model.currentStage,
    created_at: model.createdAt,
    updated_at: model.updatedAt,
    expires_at: model.deadlineAt,
    document: model.document ? fromEnvelopeDetailDocument(model.document) : null,
    participants: model.participants.map(fromParticipant),
  };
}

/** DTO cru (rede) → modelo de UI, com validação. Ponto de entrada real. */
export function parseEnvelopeDetail(raw: unknown): EnvelopeDetail {
  return toEnvelopeDetail(parseDto(ownerWebEnvelopeViewSchema, raw, 'envelope'));
}

// ---------------------------------------------------------------- envelope (summary / lista)

export function toEnvelopeSummary(dto: OwnerWebEnvelopeSummary): EnvelopeSummary {
  return {
    id: dto.id,
    status: dto.status,
    documentName: dto.document_name,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    deadlineAt: dto.expires_at,
    signerCount: dto.signer_count,
    observerCount: dto.observer_count,
    signedCount: dto.signed_count,
    participantsTotal: dto.signer_count,
    participantsSigned: dto.signed_count,
  };
}

export function fromEnvelopeSummary(model: EnvelopeSummary): OwnerWebEnvelopeSummary {
  return {
    id: model.id,
    status: model.status,
    document_name: model.documentName,
    created_at: model.createdAt,
    updated_at: model.updatedAt,
    expires_at: model.deadlineAt,
    signer_count: model.signerCount,
    observer_count: model.observerCount,
    signed_count: model.signedCount,
  };
}

/**
 * Item da LISTA global (R-UX02-P02/L3A-2b) — reusa `toEnvelopeSummary`/`fromEnvelopeSummary`
 * (mesma base do Dashboard, DI2-2) e traduz só os dois campos que só a lista tem.
 * `documentId`/`document_id` NUNCA são renomeados de outra forma — é `snake_case` ⇄
 * `camelCase` puro, sem lógica de apresentação aqui (os três casos de exibição do
 * documento vivem em `lib/copy.ts`/`EnvelopeTable.tsx`, não no adapter).
 */
export function toEnvelopeListItem(dto: OwnerWebEnvelopeListItem): EnvelopeListItem {
  return {
    ...toEnvelopeSummary(dto),
    documentId: dto.document_id,
    lastActivityAt: dto.last_activity_at,
  };
}

export function fromEnvelopeListItem(model: EnvelopeListItem): OwnerWebEnvelopeListItem {
  return {
    ...fromEnvelopeSummary(model),
    document_id: model.documentId,
    last_activity_at: model.lastActivityAt,
  };
}

export function toEnvelopeList(dto: OwnerWebEnvelopeList): EnvelopeListResponse {
  return {
    items: dto.items.map(toEnvelopeListItem),
    nextCursor: dto.next_cursor,
    total: dto.total,
    totalFiltered: dto.total_filtered,
    totalReturned: dto.total_returned,
  };
}

export function fromEnvelopeList(model: EnvelopeListResponse): OwnerWebEnvelopeList {
  return {
    items: model.items.map(fromEnvelopeListItem),
    next_cursor: model.nextCursor,
    total: model.total,
    total_filtered: model.totalFiltered,
    total_returned: model.totalReturned,
  };
}

/** DTO cru (rede) → modelo de UI, com validação **fail-closed** (`ownerWebEnvelopeListSchema`,
 * `.strict()` + as três invariantes numéricas). Ponto de entrada real de `listEnvelopes()`
 * (`endpoints.ts`) — nenhuma tela vê o DTO cru nem `items.length` no lugar de `total`. */
export function parseEnvelopeList(raw: unknown): EnvelopeListResponse {
  return toEnvelopeList(parseDto(ownerWebEnvelopeListSchema, raw, 'envelopes'));
}

export function parseEnvelopeSummary(raw: unknown): EnvelopeSummary {
  return toEnvelopeSummary(parseDto(ownerWebEnvelopeSummarySchema, raw, 'envelope'));
}

// ---------------------------------------------------------------- dashboard (R-UX02-P01/L3B)

/**
 * `due_soon` → `dueSoon` é a ÚNICA renomeação de valor (os outros cinco já eram
 * palavra única). `Record` total sobre o enum REAL (`OwnerWebDashboardAttentionKind`,
 * 6 valores) — um `kind` novo no backend não compila aqui, o que é o comportamento
 * certo: a zod já validou a resposta contra os 6 valores conhecidos antes deste
 * ponto (`zOwnerWebDashboardResponse`), então não existe "kind desconhecido" a
 * tratar em runtime — é `ContractViolationError` no `parseDto`, mais cedo.
 */
const ATTENTION_KIND_TO_UI: Record<OwnerWebDashboardAttentionKind, DashboardAttentionKind> = {
  declined: 'declined',
  expired: 'expired',
  overdue: 'overdue',
  due_soon: 'dueSoon',
  ready: 'ready',
  stalled: 'stalled',
};

const AT_KIND_TO_UI: Record<OwnerWebDashboardAtKind, DashboardAtKind> = {
  expires_at: 'expiresAt',
  updated_at: 'updatedAt',
};

/**
 * DTO → modelo de UI. Reusa `toEnvelopeSummary` (mandato §"CONTRATO DE DADOS DO
 * BROWSER") — o `envelope` de cada item de atenção/recente é o MESMO shape que
 * `GET /web/envelopes` já traduz; duplicar o mapeamento aqui divergiria em
 * silêncio mais cedo ou mais tarde.
 */
export function toDashboardSnapshot(dto: OwnerWebDashboardResponse): DashboardSnapshot {
  return {
    generatedAt: dto.generated_at,
    metrics: {
      inProgress: dto.metrics.in_progress,
      dueWithin48h: dto.metrics.due_within_48h,
      completed: dto.metrics.completed,
      attention: dto.metrics.attention,
    },
    attentionItems: dto.attention_items.map((item) => ({
      envelope: toEnvelopeSummary(item.envelope),
      kind: ATTENTION_KIND_TO_UI[item.kind],
      at: item.at,
      atKind: AT_KIND_TO_UI[item.at_kind],
    })),
    recentItems: dto.recent_items.map((item) => ({ envelope: toEnvelopeSummary(item.envelope) })),
  };
}

/**
 * DTO cru (rede) → modelo de UI, com validação **fail-closed**
 * (`zOwnerWebDashboardResponse`, `@argink/shared/owner-web` — o MESMO schema que o
 * domínio do backend produz, `.strict()` em cada nível). Ponto de entrada real de
 * `getDashboard()` (`endpoints.ts`); nenhum componente vê o DTO cru.
 */
export function parseDashboardSnapshot(raw: unknown): DashboardSnapshot {
  return toDashboardSnapshot(parseDto(zOwnerWebDashboardResponse, raw, 'dashboard'));
}

// ---------------------------------------------------------------- criação

/** Fuso IANA do navegador (ex.: `America/Sao_Paulo`). Obrigatório no payload. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function pad(value: number, length = 2): string {
  return String(Math.abs(value)).padStart(length, '0');
}

/**
 * ISO 8601 **com offset local explícito** (`2026-08-20T12:00:00.000-03:00`) — o
 * backend exige `.datetime({ offset: true })`. `toISOString()` (sempre `Z`)
 * também seria aceito, mas apaga o fuso em que o owner leu a data na revisão;
 * o prazo é uma afirmação do owner, então viaja no relógio dele.
 */
export function toIsoWithOffset(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const offset = `${sign}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(Math.abs(offsetMinutes) % 60)}`;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}` +
    offset
  );
}

/**
 * Frações do wire → rascunho de carimbo do formulário (preset + página).
 *
 * Falha fechada quando o posicionamento não corresponde a nenhum dos seis presets: o wizard só
 * sabe representar esses seis, e reidratar um rascunho com "rodapé direita" quando o servidor
 * guardou outra coordenada faria a tela AFIRMAR uma posição que não é a gravada — exatamente o
 * tipo de fallback plausível-porém-falso que a DI2-3 proíbe. Geometria livre é da `/v1`; o
 * wizard não pretende ser aquele cliente.
 */
function stampDraftFromPlacement(
  placement: VisualPlacement | null | undefined,
  path: string,
): { preset: StampPreset; page: number } | null {
  if (placement === null || placement === undefined) return null;
  const preset = stampPresetOf(placement);
  if (!preset) {
    throw new ContractViolationError(
      path,
      placement,
      'posição de carimbo fora dos presets do wizard',
    );
  }
  return { preset, page: placement.page };
}

/**
 * Form do wizard → `payload` (JSON) do multipart. Aqui morrem as três
 * incompatibilidades que impediam qualquer criação de envelope:
 *  - `documentName` e `stageMode` **não são enviados** (`.strict()` do backend
 *    rejeitaria; o nome do arquivo já viaja no multipart e o workflow é derivado
 *    dos `stage`);
 *  - observer sai com `signature_level`, `stage` e `cpf` = `null` **explícito**
 *    (o schema é `.nullable()`, não `.optional()` — campo ausente é erro);
 *  - `timezone` passa a ir junto.
 */
export function toCreateEnvelopePayloadDto(
  form: CreateEnvelopePayload,
): OwnerWebCreateEnvelopePayload {
  const participants: OwnerWebParticipantInput[] = form.participants.map((participant) => {
    if (participant.role === 'signer') {
      return {
        name: participant.name,
        email: participant.email,
        cpf: participant.cpf,
        role: 'signer',
        signature_level: participant.modality,
        stage: participant.stage,
        // Carimbo visível (D-027): a chave só EXISTE quando houve opt-in. Sem carimbo, o
        // payload sai byte a byte igual ao de antes desta rodada — que é o que mantém o
        // default intocado (e a chave de idempotência estável para quem não usa o recurso).
        // As frações nascem aqui, do preset escolhido; a tela nunca digita geometria.
        ...(participant.stamp
          ? { visual: stampPlacement(participant.stamp.preset, participant.stamp.page) }
          : {}),
      };
    }
    return {
      name: participant.name,
      email: participant.email,
      cpf: null,
      role: 'observer',
      signature_level: null,
      stage: null,
    };
  });

  return {
    participants,
    deadline_preset: form.deadlinePreset,
    expires_at: form.deadlineAt,
    timezone: form.timezone,
  };
}

/** Sentido inverso — existe para o round-trip ser testável de verdade e para
 * reidratar um rascunho vindo do wire. Recusa DTO que viole a regra de papel. */
export function fromCreateEnvelopePayloadDto(
  dto: OwnerWebCreateEnvelopePayload,
): CreateEnvelopePayload {
  return {
    deadlinePreset: dto.deadline_preset,
    deadlineAt: dto.expires_at,
    timezone: dto.timezone,
    participants: dto.participants.map((participant, index) => {
      const path = `payload.participants.${index}`;
      if (participant.role === 'signer') {
        if (participant.signature_level === null) {
          throw new ContractViolationError(
            `${path}.signature_level`,
            null,
            'signatário sem modalidade de assinatura',
          );
        }
        if (participant.stage === null) {
          throw new ContractViolationError(`${path}.stage`, null, 'signatário sem etapa');
        }
        return {
          role: 'signer' as const,
          name: participant.name,
          email: participant.email,
          modality: participant.signature_level,
          stage: participant.stage,
          cpf: participant.cpf,
          stamp: stampDraftFromPlacement(participant.visual, `${path}.visual`),
        };
      }

      if (participant.visual !== null && participant.visual !== undefined) {
        throw new ContractViolationError(
          `${path}.visual`,
          participant.visual,
          'observador não tem carimbo visível neste documento',
        );
      }

      if (participant.cpf !== null) {
        throw new ContractViolationError(
          `${path}.cpf`,
          participant.cpf,
          'observador não tem CPF neste documento',
        );
      }
      if (participant.signature_level !== null) {
        throw new ContractViolationError(
          `${path}.signature_level`,
          participant.signature_level,
          'observador não tem modalidade de assinatura',
        );
      }
      if (participant.stage !== null) {
        throw new ContractViolationError(
          `${path}.stage`,
          participant.stage,
          'observador fica fora das etapas',
        );
      }
      return {
        role: 'observer' as const,
        name: participant.name,
        email: participant.email,
      };
    }),
  };
}

// ---------------------------------------------------------------- start / resend

export function toStartEnvelopeRequestDto(input: {
  expectedUpdatedAt: string;
}): OwnerWebStartEnvelopeRequest {
  return { confirmation: true, expectedUpdatedAt: input.expectedUpdatedAt };
}

export function fromStartEnvelopeRequestDto(dto: OwnerWebStartEnvelopeRequest): {
  expectedUpdatedAt: string;
} {
  return { expectedUpdatedAt: dto.expectedUpdatedAt };
}

export function toResendRequestDto(input: { participantId: string }): OwnerWebResendRequest {
  return { participantId: input.participantId };
}

export function fromResendRequestDto(dto: OwnerWebResendRequest): { participantId: string } {
  return { participantId: dto.participantId };
}

// ---------------------------------------------------------------- evidência

/**
 * O backend NÃO manda `checkedAt`: o relatório é remontado e reverificado a cada
 * chamada. `receivedAt` é carimbado aqui, no instante da resposta — a única
 * afirmação verdadeira sobre "quando" que este cliente pode fazer.
 */
export function toVerificationReport(
  dto: VerificationReportDto,
  receivedAt: string,
): VerificationReport {
  return {
    verdict: dto.verdict,
    verifierVersion: dto.verifierVersion,
    bundleFormatId: dto.bundleFormatId,
    manifestHash: dto.manifestHash,
    manifestSchemaVersion: dto.manifestSchemaVersion,
    sealKeyId: dto.sealKeyId,
    manifest: dto.manifest ?? null,
    checks: dto.checks.map((check) => ({
      name: check.name,
      status: check.status,
      reasons: [...check.reasons],
      detail: { ...check.detail },
      notes: [...check.notes],
    })),
    summary: {
      passed: dto.summary.passed,
      failed: dto.summary.failed,
      indeterminate: dto.summary.indeterminate,
      skipped: dto.summary.skipped,
      failedChecks: [...dto.summary.failedChecks],
      indeterminateChecks: [...dto.summary.indeterminateChecks],
      reasons: [...dto.summary.reasons],
    },
    notes: [...dto.notes],
    receivedAt,
  };
}

/** `receivedAt` é carimbo do cliente e por isso não volta para o wire. */
export function fromVerificationReport(model: VerificationReport): VerificationReportDto {
  return {
    verdict: model.verdict,
    verifierVersion: model.verifierVersion,
    bundleFormatId: model.bundleFormatId,
    manifestHash: model.manifestHash,
    manifestSchemaVersion: model.manifestSchemaVersion,
    sealKeyId: model.sealKeyId,
    manifest: model.manifest,
    checks: model.checks.map((check) => ({
      name: check.name,
      status: check.status,
      reasons: [...check.reasons],
      detail: { ...check.detail },
      notes: [...check.notes],
    })),
    summary: {
      passed: model.summary.passed,
      failed: model.summary.failed,
      indeterminate: model.summary.indeterminate,
      skipped: model.summary.skipped,
      failedChecks: [...model.summary.failedChecks],
      indeterminateChecks: [...model.summary.indeterminateChecks],
      reasons: [...model.summary.reasons],
    },
    notes: [...model.notes],
  };
}

export function parseVerificationReport(raw: unknown, receivedAt: string): VerificationReport {
  return toVerificationReport(
    parseDto(verificationReportDtoSchema, raw, 'evidence') as VerificationReportDto,
    receivedAt,
  );
}

// ---------------------------------------------------------------- sessão

export function parseSessionInfo(raw: unknown): SessionInfo {
  const dto = parseDto(sessionInfoDtoSchema, raw, 'session');
  return {
    user: dto.user,
    membership: dto.membership,
    tenant: dto.tenant,
    expiresAt: dto.expires_at,
    environment: dto.environment,
    capabilities: { evidenceDelivery: dto.capabilities.evidence_delivery },
  };
}

// ---------------------------------------------------------------- validar assinado (D-063/#100)

export function parseSignedDocumentValidation(raw: unknown): SignedDocumentValidation {
  const dto = parseDto(signedDocumentValidationDtoSchema, raw, 'validate');
  return {
    result: dto.result,
    indication: dto.indication,
    subIndication: dto.sub_indication,
    signatureCount: dto.signature_count,
    checkedAt: dto.checked_at,
  };
}
